import {fileLevelCommentKey, type DiffEntry, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import type {PullRequestFileTreeDiff, ThreadMarker} from '../page-data/payloads/file-tree'
import type {Markers, MarkersByFile, MarkersInfo} from '../stores/slices/markers'
import type {
  DiffAnnotation,
  Thread as ConversationThread,
  ThreadPositioning,
} from '@github-ui/diff-lines/conversation-types'
import type {PendingReview} from '../stores/slices/pending-review'
import type {CreateReviewCommentMutationData} from '../mutations/use-create-review-comment-mutation'

export function extractAnnotationMarkerId(
  parsedAnnotationId: number,
  annotations: Record<number, Pick<DiffAnnotation, 'id' | 'databaseId'>> | undefined,
) {
  if (!parsedAnnotationId || !annotations) {
    return undefined
  }

  for (const annotation of Object.values(annotations)) {
    if (annotation && annotation.databaseId === parsedAnnotationId) {
      return annotation.id
    }
  }

  return undefined
}

export function extractCommentThreadMarkerId(parsedCommentId: number | undefined, markers: MarkersByFile | undefined) {
  if (!parsedCommentId || !markers) {
    return undefined
  }

  for (const marker of Object.values(markers)) {
    for (const thread of marker.threads) {
      for (const comment of thread.commentsData?.comments ?? []) {
        if (comment.databaseId === parsedCommentId) {
          return thread.id
        }
      }
    }
  }

  return undefined
}

export function buildDiffSummaryMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, PullRequestFileTreeDiff> {
  return diffSummaries.reduce<Record<string, PullRequestFileTreeDiff>>((buildingDiffSummaryMap, diffSummary) => {
    buildingDiffSummaryMap[diffSummary.path] = diffSummary
    return buildingDiffSummaryMap
  }, {})
}

export function buildCollapsedDiffStatusMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, boolean> {
  return diffSummaries.reduce<Record<string, boolean>>((buildingCollapsedDiffStatusMap, diffSummary) => {
    buildingCollapsedDiffStatusMap[diffSummary.path] = !!diffSummary.markedAsViewed
    return buildingCollapsedDiffStatusMap
  }, {})
}

export function buildPendingReview(pendingReviewData: PendingReview) {
  const pendingThreadIDs = pendingReviewData.comments.map(comment => parseInt(comment.threadId))
  return {
    id: pendingReviewData.id,
    pendingReviewIDs: pendingThreadIDs,
    comments: pendingReviewData.comments,
  }
}

export function buildDiffEntryMap(diffEntries: DiffEntry[]): Record<string, DiffEntry> {
  return diffEntries.reduce<Record<string, DiffEntry>>((buildingDiffEntryMap, diffEntry) => {
    buildingDiffEntryMap[diffEntry.path] = diffEntry
    return buildingDiffEntryMap
  }, {})
}

export function buildNormalizedMarkers(
  markers: Markers | undefined,
  diffSummaries: PullRequestFileTreeDiff[],
): MarkersInfo | undefined {
  if (!markers) {
    return undefined
  }

  const normalizedMarkers: MarkersInfo = {}
  if (!diffSummaries) return normalizedMarkers

  // TODO: eventually we will keep the comment counts for threads on the thread object itself, but for now, we don't
  for (const diffSummary of diffSummaries) {
    if (!diffSummary.markersMap || Object.keys(diffSummary.markersMap).length === 0) continue
    const filePath = diffSummary.path
    const currentFileMarkers: MarkersByFile = {}
    for (const markerKey in diffSummary.markersMap) {
      const diffSummaryMarker = diffSummary.markersMap[markerKey]
      if (!diffSummaryMarker?.threads && !diffSummaryMarker?.annotations) continue

      const threadsAnnotationsInLine: {threads: ConversationThread[]; annotations: DiffAnnotation[]} = {
        threads: [],
        annotations: [],
      }
      if (diffSummaryMarker.annotations) {
        for (const threadMarker of diffSummaryMarker.annotations) {
          const annotation = markers.annotations[Number(threadMarker.id)]
          if (annotation) {
            //totalCommentCountForSummary += thread.commentsData.comments.length
            threadsAnnotationsInLine.annotations.push(annotation)
          }
        }
      }
      if (diffSummaryMarker.threads) {
        for (const threadMarker of diffSummaryMarker.threads) {
          const positioning = getPositioningValuesFromThreadMarkers(markerKey, threadMarker)
          let thread = markers.threads[Number(threadMarker.id)]
          if (thread && positioning) {
            // TODO: This is a workaround to avoid the ESLint error about mutating props directly.
            // Ideally, we should refactor this to avoid direct mutation, but since we need to do a quick fix for performance, this mimics the previous behavior.

            thread = {...thread, positioning, subject: positioning} // stitch in both until we can remove subject
          }
          if (thread) {
            threadsAnnotationsInLine.threads.push(thread)
          }
        }
      }

      if (markerKey === 'FILE') {
        // because of typescript weirdness, we need to use a key that follows the FileKey type
        currentFileMarkers[fileLevelCommentKey] = threadsAnnotationsInLine
      } else {
        currentFileMarkers[markerKey as LineKey | FileKey] = threadsAnnotationsInLine
      }
    }

    normalizedMarkers[filePath] = currentFileMarkers
  }

  return normalizedMarkers
}

export function buildSubjectFromCommentData(inputs: CreateReviewCommentMutationData): ThreadPositioning | undefined {
  return {
    endLine: inputs.line,
    endDiffSide: inputs.side?.toLowerCase() === 'right' ? 'RIGHT' : 'LEFT',
    startLine: inputs.startLine ?? inputs.line,
    startDiffSide: inputs.startSide
      ? inputs.startSide?.toLowerCase() === 'right'
        ? 'RIGHT'
        : 'LEFT'
      : inputs.side?.toLowerCase() === 'right'
        ? 'RIGHT'
        : 'LEFT',
    isOutdated: false, // false because it's a new comment
  }
}

const diffSideMap = {
  L: 'LEFT',
  R: 'RIGHT',
} as const

type LineSideLetter = 'L' | 'R'

function getPositioningValuesFromThreadMarkers(
  diffSummaryKey: string,
  threadMarker: ThreadMarker,
): ThreadPositioning | undefined {
  if (diffSummaryKey === 'FILE') {
    return undefined
  }

  const endDiffSideChar = diffSummaryKey[0]
  if (!isLineSideLetter(endDiffSideChar)) {
    return undefined
  }
  const endDiffSideLetter = endDiffSideChar
  const endLineNumber = Number(diffSummaryKey.slice(1))

  let startLineNumber = -1
  let startDiffSideLetter: LineSideLetter = 'R'

  if (threadMarker.start) {
    startLineNumber = parseInt(threadMarker.start.slice(1))
    const startChar = threadMarker.start[0]
    if (isLineSideLetter(startChar)) {
      startDiffSideLetter = startChar
    }
  } else {
    startLineNumber = endLineNumber
    startDiffSideLetter = endDiffSideLetter
  }

  const endDiffSide = diffSideMap[endDiffSideLetter]
  const startDiffSide = diffSideMap[startDiffSideLetter]

  return {
    endLine: endLineNumber,
    endDiffSide,
    startLine: startLineNumber,
    startDiffSide,
    isOutdated: !!threadMarker.outdatedReason,
  }
}

function isLineSideLetter(value: string | undefined): value is LineSideLetter {
  return value === 'L' || value === 'R'
}
